import { ComponentProps } from 'react'

import { useMediaQueryContext } from 'contexts/MediaQueryContext'

import {
  useGetGlobalFeaturedCollectionsQuery,
  useGetMarketFeaturedCollectionsQuery,
} from 'hooks/useQueries'

import CollectionCard, {
  LoadingPlaceholder,
} from 'components/cards/CollectionCard'

import { InsetContainer } from 'components/layout/Container'
import PaginatedSlides from 'components/display/PaginatedSlides'

import { HeadingGroup, Title, Subtitle } from '../../shared'

const PAGE_SIZE = 3

type Collection = {
  id: string
  name: string
  url: string
  image?: {
    medium: string
    large: string
  }
  market?: { id: string; code: string; name: string }
}

const CollectionItem = ({ item }: { item: Collection }) => (
  <CollectionCard
    url={item.url}
    name={item.name}
    image={item.image}
    market={item.market}
  />
)

type FeaturedCollectionsProps = {
  collections: Collection[]
} & Omit<
  ComponentProps<typeof PaginatedSlides>,
  'items' | 'ItemComponent' | 'customTitle'
>

export const FeaturedCollections = ({
  collections,
  loading,
  called,
  canFetchMore,
  onFetchMore,
}: FeaturedCollectionsProps) => {
  // Reserve vertical space only at `lg`+, where `PaginatedSlides`
  // actually renders the desktop Embla slider. Below `lg` the mobile
  // horizontal list takes its natural ~425px height; the old
  // `largerThanMd` branch was passing the desktop reserve (618px) to
  // viewports between 768–991px and a 480px reserve to phones, both
  // of which left a persistent empty band under the cards on mobile.
  const { lg } = useMediaQueryContext()

  const minHeight = lg ? '618px' : '0'

  return (
    <InsetContainer>
      <PaginatedSlides
        items={collections}
        ItemComponent={CollectionItem}
        loading={loading}
        canFetchMore={canFetchMore}
        onFetchMore={onFetchMore}
        called={called}
        customTitle={
          <HeadingGroup>
            <Title>Explore collections</Title>
            <Subtitle style={{ marginBottom: 0 }}>
              Pick your vibe, find your escape.
            </Subtitle>
          </HeadingGroup>
        }
        loadingPlaceholder={<LoadingPlaceholder />}
        minHeight={minHeight}
      />
    </InsetContainer>
  )
}

type GlobalFeaturedCollectionsProps = {
  countryCode?: GQL_Countries
}

export const GlobalFeaturedCollections = ({
  countryCode,
}: GlobalFeaturedCollectionsProps) => {
  const { data, loading, called, fetchMore } =
    useGetGlobalFeaturedCollectionsQuery({
      variables: {
        country: countryCode,
        first: PAGE_SIZE,
      },
    })

  const collections = data?.featuredCollections?.edges?.map(({ node }) => node)

  const pageInfo = data?.featuredCollections?.pageInfo

  const canFetchMore = pageInfo?.hasNextPage

  const onFetchMore = () =>
    fetchMore({
      variables: {
        cursor: pageInfo?.endCursor,
      },
    })

  return (
    <FeaturedCollections
      collections={collections}
      loading={loading}
      called={called}
      canFetchMore={canFetchMore}
      onFetchMore={onFetchMore}
    />
  )
}

type MarketFeaturedCollectionsProps = {
  marketCode: GQL_MarketCode
}

export const MarketFeaturedCollections = ({
  marketCode,
}: MarketFeaturedCollectionsProps) => {
  const { data, loading, called, fetchMore } =
    useGetMarketFeaturedCollectionsQuery({
      variables: {
        first: PAGE_SIZE,
        market: marketCode,
      },
    })

  const collections = data?.market?.featuredCollections?.edges?.map(
    ({ node }) => node,
  )

  const pageInfo = data?.market?.featuredCollections?.pageInfo

  const canFetchMore = pageInfo?.hasNextPage

  const onFetchMore = () =>
    fetchMore({
      variables: {
        cursor: pageInfo?.endCursor,
      },
    })

  return (
    <FeaturedCollections
      collections={collections}
      loading={loading}
      called={called}
      canFetchMore={canFetchMore}
      onFetchMore={onFetchMore}
    />
  )
}

export default FeaturedCollections
